'use client';

import {useEffect} from 'react';
import {DropdownMenuTrigger} from '@radix-ui/react-dropdown-menu';
import {Table} from '@tanstack/react-table';
import {Settings2} from 'lucide-react';

import {Button} from '@/shadcn_components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/shadcn_components/ui/dropdown-menu';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  storageKey?: string; // Optional key for localStorage
}

export function DataTableViewOptions<TData>({table, storageKey}: DataTableViewOptionsProps<TData>) {
  useEffect(() => {
    // initialize column visibility based on initial state and localStorage
    table.getAllColumns().forEach(column => {
      if (table.initialState.columnVisibility[column.id] === false) {
        column.toggleVisibility(false); // Ensure initial visibility state is applied
      }
    });

    if (storageKey) {
      const savedVisibility = localStorage.getItem(storageKey);
      if (savedVisibility) {
        const visibilityState = JSON.parse(savedVisibility);
        table.getAllColumns().forEach(column => {
          if (visibilityState[column.id] !== undefined) {
            column.toggleVisibility(visibilityState[column.id]);
          }
        });
      }
    }
  }, [storageKey, table]);

  const saveVisibility = (columnId: string, visible: boolean) => {
    if (!storageKey) return; // No storage key provided, do nothing

    const savedVisibility = localStorage.getItem(storageKey);
    const visibilityState = savedVisibility ? JSON.parse(savedVisibility) : {};
    visibilityState[columnId] = visible;
    localStorage.setItem(storageKey, JSON.stringify(visibilityState));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 lg:flex">
          <Settings2 />
          Ẩn/hiện cột
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <DropdownMenuLabel>Ẩn/hiện cột</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter(column => typeof column.accessorFn !== 'undefined' && column.getCanHide())
          .map(column => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="capitalize"
                checked={column.getIsVisible()}
                onCheckedChange={value => {
                  column.toggleVisibility(!!value);
                  saveVisibility(column.id, !!value);
                }}>
                {column.columnDef.meta?.label || column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
